import React, { useMemo, useState } from 'react';
import { styles } from '../constants/styles';
import { aktifKumbaralariFiltrele } from '../utils/aktifKumbaraHesapla';
import KumbaraItem from './KumbaraItem';
import ArsivSistemi from './ArsivSistemi';
import { haversineDistance } from '../utils/haversine';

const YoneticiAnasayfa = ({
  kumbaralar = [],
  teslimEdilenler = [],
  acilmisKumbaralar = [],
  dagitimdaBekleyen = 0,
  sahaKonum = null,
  setKumbaralar = () => {},
  setTeslimEdilenler = () => {}
}) => {
  const [aktifTab, setAktifTab] = useState('genelbakis');
  const [durumFiltre, setDurumFiltre] = useState('Hepsi');
  const [alanFiltre, setAlanFiltre] = useState('Hepsi');
  const [siralama, setSiralama] = useState('firmaAdi');
  const [arama, setArama] = useState('');
  const [kumbaraSayfa, setKumbaraSayfa] = useState(1);
  const [gecikmeListeSayfa, setGecikmeListeSayfa] = useState(1);
  const SAYFA_BASI = 10;

  const aktifKumbaralar = useMemo(() => {
    return aktifKumbaralariFiltrele(
      Array.isArray(kumbaralar) ? kumbaralar : [],
      Array.isArray(acilmisKumbaralar) ? acilmisKumbaralar : []
    );
  }, [kumbaralar, acilmisKumbaralar]);

  const doluSayisi = aktifKumbaralar.filter(k => k.durum === 'Dolu').length;

  const gecikme = aktifKumbaralar.filter(k =>
    Math.floor((Date.now() - k.sonZiyaret) / (1000 * 60 * 60 * 24)) > 15
  );

  const bugunAcilanGelir = useMemo(() => {
    const bugun = new Date();
    const bugunKey = bugun.getFullYear() * 10000 + (bugun.getMonth() + 1) * 100 + bugun.getDate();
    return acilmisKumbaralar
      .filter(a => {
        const t = a.acilisTarihi || a.acilmaTarihi || a.tarih;
        if (!t) return false;
        const d = t.toDate ? t.toDate() : new Date(t);
        return d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate() === bugunKey;
      })
      .reduce((sum, a) => sum + (parseFloat(a.tutar) || 0), 0);
  }, [acilmisKumbaralar]);

  const toplamTutar = useMemo(() =>
    acilmisKumbaralar.reduce((sum, k) => sum + (parseFloat(k.tutar) || 0), 0),
    [acilmisKumbaralar]
  );

  const mevcutAlanlar = useMemo(() => {
    const alanlar = new Set();
    aktifKumbaralar.forEach(k => { if (k.alan || k.bolge) alanlar.add(k.alan || k.bolge); });
    return Array.from(alanlar).sort();
  }, [aktifKumbaralar]);

  // Harita iframe src güncellemesini debounce et: sahaKonum değişince 5 saniye bekle
  const [debouncedSahaKonum, setDebouncedSahaKonum] = useState(sahaKonum);
  const haritaDebounceRef = React.useRef(null);

  React.useEffect(() => {
    if (haritaDebounceRef.current) clearTimeout(haritaDebounceRef.current);
    haritaDebounceRef.current = setTimeout(() => {
      setDebouncedSahaKonum(sahaKonum);
    }, 5000);
    return () => clearTimeout(haritaDebounceRef.current);
  }, [sahaKonum]);

  const haritaSrc = useMemo(() => {
    if (!debouncedSahaKonum || !debouncedSahaKonum.lat || !debouncedSahaKonum.lon) return '';
    return `https://maps.google.com/maps?q=${debouncedSahaKonum.lat},${debouncedSahaKonum.lon}&z=15&output=embed`;
  }, [debouncedSahaKonum]);

  const yakindakiKumbaralar = useMemo(() => {
    if (!sahaKonum?.lat || !sahaKonum?.lon) return aktifKumbaralar;
    return aktifKumbaralar
      .map(k => {
        const mesafe = haversineDistance(sahaKonum.lat, sahaKonum.lon, parseFloat(k.lat) || null, parseFloat(k.lon) || null);
        return { ...k, mesafe: mesafe !== null ? mesafe : Infinity };
      })
      .sort((a, b) => a.mesafe - b.mesafe);
  }, [aktifKumbaralar, sahaKonum]);

  const filtrelenmisKumbaralar = useMemo(() => {
    const taban = siralama === 'yakin' ? yakindakiKumbaralar : aktifKumbaralar;
    let f = taban;
    if (durumFiltre !== 'Hepsi') f = f.filter(k => k.durum === durumFiltre);
    if (alanFiltre !== 'Hepsi') f = f.filter(k => (k.alan || k.bolge) === alanFiltre);
    if (arama.trim()) {
      const q = arama.toLowerCase();
      f = f.filter(k =>
        (k.firmaAdi || '').toLowerCase().includes(q) ||
        (k.adres || '').toLowerCase().includes(q) ||
        (k.id || '').toString().includes(q) ||
        (k.alan || k.bolge || '').toLowerCase().includes(q)
      );
    }
    if (siralama !== 'yakin') {
      f = [...f].sort((a, b) => {
        if (siralama === 'sonZiyaret') return (a.sonZiyaret || 0) - (b.sonZiyaret || 0);
        if (siralama === 'durum') {
          const d = { 'Dolu': 1, 'Yarım Dolu': 2, 'Boş': 3, 'Hiç Dolmuyor': 4 };
          return (d[a.durum] || 99) - (d[b.durum] || 99);
        }
        if (siralama === 'firmaAdi') return (a.firmaAdi || '').localeCompare(b.firmaAdi || '', 'tr');
        return 0;
      });
    }
    return f;
  }, [aktifKumbaralar, yakindakiKumbaralar, durumFiltre, alanFiltre, arama, siralama]);

  React.useEffect(() => { setKumbaraSayfa(1); }, [durumFiltre, alanFiltre, arama, siralama]);

  const btnStyle = (disabled, color) => ({
    padding: '8px 18px',
    border: `2px solid ${color}`,
    borderRadius: '7px',
    background: disabled ? '#f7fafc' : color,
    color: disabled ? '#a0aec0' : 'white',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontWeight: 'bold',
    fontSize: '13px'
  });

  return (
    <>
      <style>{`
        .ya-tab-short { display: none; }
        @media (max-width: 600px) {
          .ya-tab-full { display: none; }
          .ya-tab-short { display: inline; }
        }
        .ya-stats {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 16px;
          margin-bottom: 24px;
        }
        .ya-card {
          background: white;
          border-radius: 12px;
          padding: 20px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-left: 4px solid #e2e8f0;
        }
        .ya-card-label { color: #718096; font-size: 13px; margin-bottom: 4px; }
        .ya-card-value { font-size: 28px; font-weight: bold; margin: 0; }
        .ya-card-icon { font-size: 32px; }
        @media (max-width: 600px) {
          .ya-stats { grid-template-columns: 1fr 1fr; gap: 10px; }
          .ya-card { padding: 14px 12px; }
          .ya-card-label { font-size: 11px; }
          .ya-card-value { font-size: 22px; }
          .ya-card-icon { font-size: 26px; }
        }
        .kfy-filtreler {
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin-bottom: 20px;
        }
        .kfy-arama { width: 100%; }
        .kfy-dropdowns { display: flex; gap: 10px; }
        .kfy-dropdown { flex: 1; min-width: 0; }
        .kfy-input, .kfy-select {
          width: 100%;
          min-height: 40px;
          padding: 8px 10px;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          font-size: 14px;
          box-sizing: border-box;
        }
        @media (max-width: 600px) {
          .kfy-dropdowns { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
        }
        @media (max-width: 600px) {
          .ya-tab-bar {
            overflow-x: auto;
            white-space: nowrap;
            -webkit-overflow-scrolling: touch;
          }
          .ya-tab-bar button {
            flex-shrink: 0;
            white-space: nowrap;
          }
        }
      `}</style>

      {/* 1. İstatistik Kartları */}
      <div className="ya-stats">
        <div className="ya-card" style={{borderLeftColor: '#284D3A'}}>
          <div>
            <p className="ya-card-label">Toplam</p>
            <p className="ya-card-value" style={{color: '#284D3A'}}>{aktifKumbaralar.length}</p>
          </div>
          <span className="ya-card-icon">📦</span>
        </div>

        <div className="ya-card" style={{borderLeftColor: '#1D9E75'}}>
          <div>
            <p className="ya-card-label">Dolu</p>
            <p className="ya-card-value" style={{color: '#1D9E75'}}>{doluSayisi}</p>
          </div>
          <span className="ya-card-icon">✅</span>
        </div>

        <div
          className="ya-card"
          style={{borderLeftColor: '#E24B4A', cursor: gecikme.length > 0 ? 'pointer' : 'default'}}
          onClick={() => gecikme.length > 0 && setAktifTab('gecikmeli')}
        >
          <div>
            <p className="ya-card-label">Gecikmeli</p>
            <p className="ya-card-value" style={{color: '#E24B4A'}}>{gecikme.length}</p>
          </div>
          <span className="ya-card-icon">⚠️</span>
        </div>

        <div className="ya-card" style={{borderLeftColor: '#EF9F27'}}>
          <div>
            <p className="ya-card-label">Bugün Açılan</p>
            <p className="ya-card-value" style={{color: '#EF9F27', fontSize: bugunAcilanGelir > 0 ? '18px' : '28px'}}>
              {bugunAcilanGelir.toLocaleString('tr-TR')} ₺
            </p>
          </div>
          <span className="ya-card-icon">💳</span>
        </div>
      </div>

      {/* 2. Harita */}
      {sahaKonum && (
        <div style={{...styles.section, marginBottom: '20px'}}>
          <h3 style={{fontSize: '18px', fontWeight: 'bold', marginBottom: '15px'}}>📍 Konumunuz</h3>
          <div style={{background: '#ebf8ff', padding: '20px', borderRadius: '10px', textAlign: 'center'}}>
            <p style={{marginBottom: '15px'}}>Enlem: {sahaKonum.lat.toFixed(4)} | Boylam: {sahaKonum.lon.toFixed(4)}</p>
            <iframe
              title="Harita"
              width="100%"
              height="200"
              style={{border: 0, borderRadius: '10px'}}
              src={haritaSrc}
            />
          </div>
        </div>
      )}

      {/* 3. Tab Bar */}
      <div className="ya-tab-bar" style={{display: 'flex', gap: '8px', marginBottom: '0', borderBottom: '2px solid #e2e8f0', overflowY: 'hidden', height: '48px', alignItems: 'center'}}>
        <button
          onClick={() => setAktifTab('genelbakis')}
          style={{
            padding: '12px 20px',
            background: aktifTab === 'genelbakis' ? '#284D3A' : 'white',
            color: aktifTab === 'genelbakis' ? 'white' : '#4a5568',
            border: 'none',
            borderBottom: aktifTab === 'genelbakis' ? '2px solid #284D3A' : '2px solid transparent',
            borderRadius: '8px 8px 0 0',
            cursor: 'pointer',
            fontWeight: 'bold',
            fontSize: '14px',
            marginBottom: '-2px',
            transition: 'all 0.15s'
          }}
        >📊 <span className="ya-tab-full">Genel Bakış</span><span className="ya-tab-short">Genel</span></button>

        <button
          onClick={() => setAktifTab('kumbaralar')}
          style={{
            padding: '12px 20px',
            background: aktifTab === 'kumbaralar' ? '#284D3A' : 'white',
            color: aktifTab === 'kumbaralar' ? 'white' : '#4a5568',
            border: 'none',
            borderBottom: aktifTab === 'kumbaralar' ? '2px solid #284D3A' : '2px solid transparent',
            borderRadius: '8px 8px 0 0',
            cursor: 'pointer',
            fontWeight: 'bold',
            fontSize: '14px',
            marginBottom: '-2px',
            transition: 'all 0.15s'
          }}
        >
          📦 Kumbaralar
          <span style={{
            marginLeft: '8px',
            background: aktifTab === 'kumbaralar' ? 'rgba(255,255,255,0.25)' : '#c6f6d5',
            color: aktifTab === 'kumbaralar' ? 'white' : '#284D3A',
            borderRadius: '10px',
            padding: '1px 8px',
            fontSize: '12px',
            fontWeight: 'bold'
          }}>{filtrelenmisKumbaralar.length}</span>
        </button>

        <button
          onClick={() => setAktifTab('gecikmeli')}
          style={{
            padding: '12px 20px',
            background: aktifTab === 'gecikmeli' ? '#E24B4A' : 'white',
            color: aktifTab === 'gecikmeli' ? 'white' : (gecikme.length > 0 ? '#E24B4A' : '#4a5568'),
            border: 'none',
            borderBottom: aktifTab === 'gecikmeli' ? '2px solid #E24B4A' : '2px solid transparent',
            borderRadius: '8px 8px 0 0',
            cursor: 'pointer',
            fontWeight: 'bold',
            fontSize: '14px',
            marginBottom: '-2px',
            transition: 'all 0.15s'
          }}
        >
          ⚠️ Gecikmeli
          {gecikme.length > 0 && (
            <span style={{
              marginLeft: '8px',
              background: aktifTab === 'gecikmeli' ? 'rgba(255,255,255,0.25)' : '#fed7d7',
              color: aktifTab === 'gecikmeli' ? 'white' : '#742a2a',
              borderRadius: '10px',
              padding: '1px 8px',
              fontSize: '12px',
              fontWeight: 'bold'
            }}>{gecikme.length}</span>
          )}
        </button>

        <button
          onClick={() => setAktifTab('arsiv')}
          style={{
            padding: '12px 20px',
            background: aktifTab === 'arsiv' ? '#A26F29' : 'white',
            color: aktifTab === 'arsiv' ? 'white' : '#4a5568',
            border: 'none',
            borderBottom: aktifTab === 'arsiv' ? '2px solid #A26F29' : '2px solid transparent',
            borderRadius: '8px 8px 0 0',
            cursor: 'pointer',
            fontWeight: 'bold',
            fontSize: '14px',
            marginBottom: '-2px',
            transition: 'all 0.15s'
          }}
        >
          🗂️ Arşiv
        </button>
      </div>

      {/* Tab İçerikleri */}
      <div style={{...styles.section, borderRadius: '0 8px 8px 8px'}}>

        {/* Genel Bakış */}
        {aktifTab === 'genelbakis' && (
          <>
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px', marginBottom: '20px'}}>
              <div>
                <h3 style={{fontSize: '18px', fontWeight: 'bold', marginBottom: '16px'}}>📍 Alan Dağılımı</h3>
                {Object.entries(
                  (Array.isArray(kumbaralar) ? kumbaralar : []).reduce((acc, k) => {
                    const alan = k.alan || k.bolge || 'Belirtilmemiş';
                    acc[alan] = (acc[alan] || 0) + 1;
                    return acc;
                  }, {})
                ).map(([alan, sayi]) => (
                  <div key={alan} style={{display: 'flex', justifyContent: 'space-between', padding: '12px', background: '#f7fafc', borderRadius: '8px', marginBottom: '8px'}}>
                    <span style={{fontWeight: '500'}}>{alan}</span>
                    <span style={{fontWeight: 'bold', color: '#284D3A'}}>{sayi}</span>
                  </div>
                ))}
              </div>

              <div>
                <h3 style={{fontSize: '18px', fontWeight: 'bold', marginBottom: '16px'}}>💵 Toplam Gelir</h3>
                <div style={{textAlign: 'center', padding: '30px 0'}}>
                  <p style={{fontSize: '48px', fontWeight: 'bold', color: '#284D3A', margin: 0}}>
                    {toplamTutar.toLocaleString('tr-TR')} ₺
                  </p>
                  <p style={{color: '#718096', marginTop: '10px'}}>{acilmisKumbaralar.length} Kumbara Açıldı</p>
                </div>
              </div>
            </div>

            {acilmisKumbaralar.length > 0 && (
              <>
                <h3 style={{fontSize: '18px', fontWeight: 'bold', marginBottom: '16px'}}>💰 Son Açılan Kumbaralar</h3>
                <div style={{maxHeight: '400px', overflowY: 'auto'}}>
                  {acilmisKumbaralar
                    .slice()
                    .sort((a, b) => {
                      const tA = a.acilmaTarihi || a.acilisTarihi || a.tarih;
                      const tB = b.acilmaTarihi || b.acilisTarihi || b.tarih;
                      const dA = tA?.toDate ? tA.toDate() : new Date(tA || 0);
                      const dB = tB?.toDate ? tB.toDate() : new Date(tB || 0);
                      return dB - dA;
                    })
                    .slice(0, 20)
                    .map((acilan, index) => {
                      const tField = acilan.acilmaTarihi || acilan.acilisTarihi || acilan.tarih;
                      const tarih = tField?.toDate ? tField.toDate() : new Date(tField || new Date());
                      return (
                        <div key={acilan.firestoreId || index} style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', background: '#f7fafc', borderRadius: '8px', marginBottom: '8px', border: '1px solid #e2e8f0'}}>
                          <div style={{flex: 1}}>
                            <div style={{fontWeight: 'bold', color: '#2d3748', marginBottom: '4px'}}>
                              {acilan.kumbaraId || acilan.id || 'Bilinmeyen'} — {acilan.firmaAdi || acilan.ad || 'İsimsiz'}
                            </div>
                            <div style={{fontSize: '12px', color: '#718096'}}>
                              {tarih.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                              {acilan.alan && ` • ${acilan.alan}`}
                            </div>
                          </div>
                          <div style={{fontSize: '18px', fontWeight: 'bold', color: '#284D3A', marginLeft: '15px'}}>
                            {parseFloat(acilan.tutar || 0).toLocaleString('tr-TR')} ₺
                          </div>
                        </div>
                      );
                    })
                  }
                </div>
                {acilmisKumbaralar.length > 20 && (
                  <p style={{color: '#718096', fontSize: '12px', marginTop: '10px', textAlign: 'center'}}>
                    Toplam {acilmisKumbaralar.length} açılan kumbara (son 20 gösteriliyor)
                  </p>
                )}
              </>
            )}
          </>
        )}

        {/* Kumbaralar */}
        {aktifTab === 'kumbaralar' && (
          <>
            <div className="kfy-filtreler">
              <div className="kfy-arama">
                <label style={{display: 'block', fontSize: '12px', color: '#718096', marginBottom: '5px'}}>🔍 Arama</label>
                <input
                  type="text"
                  placeholder="Firma, adres, ID..."
                  value={arama}
                  onChange={(e) => setArama(e.target.value)}
                  className="kfy-input"
                />
              </div>
              <div className="kfy-dropdowns">
                <div className="kfy-dropdown">
                  <label style={{display: 'block', fontSize: '12px', color: '#718096', marginBottom: '5px'}}>📊 Durum</label>
                  <select value={durumFiltre} onChange={(e) => setDurumFiltre(e.target.value)} className="kfy-select">
                    <option value="Hepsi">Hepsi</option>
                    <option value="Dolu">Dolu</option>
                    <option value="Yarım Dolu">Yarım Dolu</option>
                    <option value="Boş">Boş</option>
                    <option value="Hiç Dolmuyor">Hiç Dolmuyor</option>
                  </select>
                </div>
                {mevcutAlanlar.length > 0 && (
                  <div className="kfy-dropdown">
                    <label style={{display: 'block', fontSize: '12px', color: '#718096', marginBottom: '5px'}}>📍 Alan</label>
                    <select value={alanFiltre} onChange={(e) => setAlanFiltre(e.target.value)} className="kfy-select">
                      <option value="Hepsi">Hepsi</option>
                      {mevcutAlanlar.map(alan => (
                        <option key={alan} value={alan}>{alan}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="kfy-dropdown">
                  <label style={{display: 'block', fontSize: '12px', color: '#718096', marginBottom: '5px'}}>🔄 Sıralama</label>
                  <select value={siralama} onChange={(e) => setSiralama(e.target.value)} className="kfy-select">
                    <option value="firmaAdi">Firma Adı (A-Z)</option>
                    <option value="sonZiyaret">Son Ziyaret (Eski Önce)</option>
                    <option value="durum">Durum (Dolu Önce)</option>
                    {sahaKonum && <option value="yakin">📍 Yakınımdaki</option>}
                  </select>
                </div>
              </div>
            </div>

            {(() => {
              const toplamSayfa = Math.ceil(filtrelenmisKumbaralar.length / SAYFA_BASI);
              const sayfaVerisi = filtrelenmisKumbaralar.slice((kumbaraSayfa - 1) * SAYFA_BASI, kumbaraSayfa * SAYFA_BASI);

              if (filtrelenmisKumbaralar.length === 0) {
                return (
                  <div style={{textAlign: 'center', padding: '40px', color: '#718096'}}>
                    <p>Filtreleme kriterlerinize uygun kumbara bulunamadı.</p>
                    <button
                      onClick={() => { setDurumFiltre('Hepsi'); setAlanFiltre('Hepsi'); setArama(''); }}
                      style={{marginTop: '15px', padding: '10px 20px', background: '#284D3A', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px'}}
                    >Filtreleri Temizle</button>
                  </div>
                );
              }

              return (
                <>
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px'}}>
                    <p style={{color: '#718096', fontSize: '14px', margin: 0}}>
                      <strong>{filtrelenmisKumbaralar.length}</strong> kumbara
                      {filtrelenmisKumbaralar.length !== aktifKumbaralar.length && (
                        <span> (toplam {aktifKumbaralar.length})</span>
                      )}
                      {toplamSayfa > 1 && (
                        <span style={{color: '#A26F29', marginLeft: '8px', fontWeight: 'bold'}}>
                          · Sayfa {kumbaraSayfa}/{toplamSayfa}
                        </span>
                      )}
                    </p>
                  </div>

                  {sayfaVerisi.map((k, index) => (
                    <KumbaraItem
                      key={k.id}
                      kumbara={k}
                      kumbaralar={kumbaralar}
                      setKumbaralar={setKumbaralar}
                      siraNo={(kumbaraSayfa - 1) * SAYFA_BASI + index + 1}
                      teslimEdilenler={teslimEdilenler}
                      setTeslimEdilenler={setTeslimEdilenler}
                    />
                  ))}

                  {toplamSayfa > 1 && (
                    <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e2e8f0'}}>
                      <button
                        onClick={() => { setKumbaraSayfa(p => Math.max(1, p - 1)); window.scrollTo({top: 0, behavior: 'smooth'}); }}
                        disabled={kumbaraSayfa === 1}
                        style={btnStyle(kumbaraSayfa === 1, '#284D3A')}
                      >‹ Önceki</button>
                      <span style={{fontSize: '13px', color: '#4a5568', fontWeight: 'bold', padding: '0 8px'}}>
                        {kumbaraSayfa} / {toplamSayfa}
                      </span>
                      <button
                        onClick={() => { setKumbaraSayfa(p => Math.min(toplamSayfa, p + 1)); window.scrollTo({top: 0, behavior: 'smooth'}); }}
                        disabled={kumbaraSayfa === toplamSayfa}
                        style={btnStyle(kumbaraSayfa === toplamSayfa, '#284D3A')}
                      >Sonraki ›</button>
                    </div>
                  )}
                </>
              );
            })()}
          </>
        )}

        {/* Gecikmeli */}
        {aktifTab === 'gecikmeli' && (() => {
          if (gecikme.length === 0) {
            return (
              <div style={{textAlign: 'center', padding: '40px', color: '#718096'}}>
                <p style={{fontSize: '32px', marginBottom: '10px'}}>✅</p>
                <p>Gecikmeli ziyaret yok. Tüm kumbaralar zamanında ziyaret edilmiş.</p>
              </div>
            );
          }
          const gecikmeToplamSayfa = Math.ceil(gecikme.length / SAYFA_BASI);
          const gecikmeListeVerisi = gecikme.slice((gecikmeListeSayfa - 1) * SAYFA_BASI, gecikmeListeSayfa * SAYFA_BASI);
          return (
            <>
              {gecikmeListeVerisi.map(k => (
                <div key={k.id} style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: '#fff5f5', border: '1px solid #fed7d7', borderRadius: '8px', marginBottom: '8px'}}>
                  <span style={{fontWeight: 'bold', color: '#E24B4A'}}>• {k.firmaAdi}</span>
                  <span style={{background: '#E24B4A', color: 'white', borderRadius: '12px', padding: '2px 10px', fontSize: '13px', fontWeight: 'bold', whiteSpace: 'nowrap'}}>
                    {Math.floor((Date.now() - k.sonZiyaret) / (1000 * 60 * 60 * 24))} gün
                  </span>
                </div>
              ))}
              {gecikmeToplamSayfa > 1 && (
                <div style={{display: 'flex', alignItems: 'center', gap: '10px', marginTop: '15px', justifyContent: 'center'}}>
                  <button
                    onClick={() => setGecikmeListeSayfa(p => Math.max(1, p - 1))}
                    disabled={gecikmeListeSayfa === 1}
                    style={btnStyle(gecikmeListeSayfa === 1, '#E24B4A')}
                  >‹ Önceki</button>
                  <span style={{fontSize: '13px', color: '#E24B4A', fontWeight: 'bold'}}>
                    {gecikmeListeSayfa} / {gecikmeToplamSayfa}
                  </span>
                  <button
                    onClick={() => setGecikmeListeSayfa(p => Math.min(gecikmeToplamSayfa, p + 1))}
                    disabled={gecikmeListeSayfa === gecikmeToplamSayfa}
                    style={btnStyle(gecikmeListeSayfa === gecikmeToplamSayfa, '#E24B4A')}
                  >Sonraki ›</button>
                </div>
              )}
            </>
          );
        })()}

        {/* Arşiv */}
        {aktifTab === 'arsiv' && (
          <ArsivSistemi kumbaralar={kumbaralar} acilmisKumbaralar={acilmisKumbaralar} />
        )}

      </div>
    </>
  );
};

export default YoneticiAnasayfa;
